import { useState } from "react";
import { Card } from "@/components/ui/card";
import { DollarSign, TrendingUp, Target, ChevronDown } from "lucide-react";

const UnifiedRevenueDashboard = ({ goal, current, status, progressPercentage }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const remaining = goal - current;
  const dataPoints = [20, 35, 28, 45, 38, 55, 48, 62, 58, 70];
  const maxValue = Math.max(...dataPoints);

  return (
    <Card 
      className="bg-gradient-to-br from-[#0f1419] to-[#1a1f24] border-gray-800/50 cursor-pointer transition-all duration-300 overflow-hidden"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      onClick={() => setIsExpanded(!isExpanded)}
      data-testid="unified-revenue-dashboard"
      style={{ height: isExpanded ? 'auto' : '120px' }}
    >
      <div className="p-8">
      {/* Collapsed Header - Always Visible */}
      <div className={`flex items-center justify-between transition-all duration-300 ${isExpanded ? 'mb-8' : 'mb-0'}`}>
        <div className="flex items-center gap-4">
          <div className="flex items-baseline gap-2">
            <DollarSign className="h-8 w-8 text-[#3dd9c5]" />
            <span className="text-5xl font-bold text-white">
              {(goal / 1000000).toFixed(1)}M
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-8 w-px bg-gray-700"></div>
            <div>
              <p className="text-sm text-gray-400">Progress</p>
              <p className="text-2xl font-bold text-[#3dd9c5]">{Math.round(progressPercentage)}%</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3dd9c5]/10 border border-[#3dd9c5]/30 rounded-full">
            <div className={`h-2 w-2 rounded-full ${
              status === "On Pace" ? "bg-[#3dd9c5]" : 
              status === "Needs Acceleration" ? "bg-red-400" : 
              "bg-green-400"
            } animate-pulse`}></div>
            <span className={`text-sm font-semibold ${
              status === "On Pace" ? "text-[#3dd9c5]" : 
              status === "Needs Acceleration" ? "text-red-400" : 
              "text-green-400"
            }`}>
              {status}
            </span>
          </div>
          <ChevronDown 
            className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`transition-all duration-300 ${isExpanded ? 'opacity-100 max-h-[500px]' : 'opacity-0 max-h-0'}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-6">
        {/* Left: Progress Gauge */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative" style={{ width: "180px", height: "90px" }}>
            <svg className="w-full h-full" viewBox="0 0 200 100">
              {/* Background arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="#1a1a1b"
                strokeWidth="16"
                strokeLinecap="round"
              />
              {/* Progress arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="url(#gaugeGradient)"
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray={`${(Math.min(100, progressPercentage) / 100) * 251.2} 251.2`}
              />
              <defs>
                <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3dd9c5" />
                  <stop offset="100%" stopColor="#2dd4bf" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center mt-4">
                <p className="text-3xl font-bold text-white">{Math.round(progressPercentage)}%</p>
                <p className="text-xs text-gray-400 mt-1">Progress</p>
              </div>
            </div>
          </div>
        </div>

        {/* Center: Revenue Breakdown */}
        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400">Current Revenue</span>
              <TrendingUp className="h-4 w-4 text-[#3dd9c5]" />
            </div>
            <p className="text-2xl font-bold text-white">${(current / 1000).toFixed(0)}K</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400">Remaining</span>
              <Target className="h-4 w-4 text-[#3dd9c5]" />
            </div>
            <p className="text-2xl font-bold text-[#3dd9c5]">${(remaining / 1000).toFixed(0)}K</p>
          </div>
        </div>

        {/* Right: Performance Chart */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-gray-400">Performance Trend</p>
              <p className="text-sm font-bold text-[#3dd9c5]">+24% Growth</p>
            </div>
          </div>
          <div className="relative h-32">
            <svg className="w-full h-full" viewBox="0 0 300 120" preserveAspectRatio="none">
              {/* Gradient Definition */}
              <defs>
                <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3dd9c5" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#3dd9c5" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Area */}
              <path
                d={`M 0 ${120 - (dataPoints[0] / maxValue) * 100} ${dataPoints
                  .map((val, idx) => `L ${(idx / (dataPoints.length - 1)) * 300} ${120 - (val / maxValue) * 100}`)
                  .join(' ')} L 300 120 L 0 120 Z`}
                fill="url(#chartGradient)"
              />

              {/* Line */}
              <path
                d={`M 0 ${120 - (dataPoints[0] / maxValue) * 100} ${dataPoints
                  .map((val, idx) => `L ${(idx / (dataPoints.length - 1)) * 300} ${120 - (val / maxValue) * 100}`)
                  .join(' ')}`}
                fill="none"
                stroke="#3dd9c5"
                strokeWidth="2"
              />

              {/* Data points */}
              {dataPoints.map((val, idx) => (
                <circle
                  key={idx}
                  cx={(idx / (dataPoints.length - 1)) * 300}
                  cy={120 - (val / maxValue) * 100}
                  r="3"
                  fill="#3dd9c5"
                />
              ))}
            </svg>
          </div>
          <p className="text-xs text-gray-500 mt-2">Last 10 periods</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-400">Overall Progress</span>
          <span className="text-xs font-semibold text-[#3dd9c5]">{Math.round(progressPercentage)}% Complete</span>
        </div>
        <div className="w-full bg-gray-800/50 rounded-full h-3 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#3dd9c5] to-[#2dd4bf] transition-all duration-500 relative"
            style={{ width: `${Math.min(100, progressPercentage)}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-shimmer" 
              style={{
                backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                backgroundSize: '200% 100%'
              }}
            ></div>
          </div>
        </div>
      </div>
      </div>
      </div>
    </Card>
  );
};

export default UnifiedRevenueDashboard;
